// DocMeta.tsx — a document's provenance, as one bordered control in parts.
//
// Every generated page carries a comment naming when it was written, which
// model wrote it, and a hash of the input it was written from. That comment
// used to render as the first line of the document, which is how machine
// metadata looks when nobody has decided where it goes: `<!-- docdaemon
// ts:2026-08-15T01:45:38Z hash:258d1b72b91b6ed1 model:qwen3.6:27b-awq -->`,
// above the heading, in the reader's way.
//
// It belongs above the prose rather than in the card footer because it is what
// tells you whether to trust what follows — a page written by the overflow
// model during a two-thousand-job backlog is a different artifact from one the
// pinned local model wrote, and the input hash is what says whether it still
// describes the code. Read before the document, not after it.
//
// Three segments in one border, because they are three facts about one thing:
// separate pills would say they were unrelated. Each does something rather
// than only reporting — the hash copies, the model filters, the age is the
// absolute timestamp on hover — so the strip earns being a control.
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { BsCheck2, BsClipboard, BsCpu, BsClock } from 'react-icons/bs';
import type { DocBlob } from '../api';
import { absoluteTime, relativeTime } from '../format';

export default function DocMeta({ blob, onModelClick }: {
  blob: DocBlob;
  // Optional: the docs route has nowhere to filter by model, but the run and
  // cost views do, so the segment is a link there and inert here rather than
  // being two different components.
  onModelClick?: (model: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyHash = () => {
    if (!blob.content_hash) return;
    void navigator.clipboard?.writeText(blob.content_hash).then(
      () => {
        setCopied(true);
        // Reverts on its own: a tick that stays forever stops meaning "just
        // now" and starts looking like a state the row is stuck in.
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => setCopied(false),
    );
  };

  return (
    <ButtonGroup size="sm" className="doc-meta mb-3" aria-label="Document provenance">
      <Button
        variant="outline-secondary"
        className="doc-meta-part"
        title={absoluteTime(blob.generated_at)}
        // Inert: the timestamp's whole affordance is the tooltip carrying the
        // absolute time behind the relative one.
        tabIndex={-1}
      >
        <span className="d-inline-flex align-items-center gap-2">
          <BsClock />
          <span className="doc-meta-label">written</span>
          {relativeTime(blob.generated_at)}
        </span>
      </Button>

      <Button
        variant="outline-secondary"
        className="doc-meta-part"
        onClick={onModelClick ? () => onModelClick(blob.model) : undefined}
        disabled={!onModelClick}
        title={blob.model ? `Written by ${blob.model}` : 'The model was not recorded on this page'}
      >
        <span className="d-inline-flex align-items-center gap-2">
          <BsCpu />
          <span className="doc-meta-label">model</span>
          <code className="doc-meta-value">{blob.model || 'unrecorded'}</code>
        </span>
      </Button>

      <Button
        variant="outline-secondary"
        className="doc-meta-part"
        onClick={copyHash}
        disabled={!blob.content_hash}
        title={blob.content_hash
          ? 'Hash of the input this page was written from — copy'
          : 'This page records no input hash, so it will be regenerated on the next run'}
      >
        <span className="d-inline-flex align-items-center gap-2">
          {copied ? <BsCheck2 /> : <BsClipboard />}
          <span className="doc-meta-label">input</span>
          <code className="doc-meta-value">{blob.content_hash || 'none'}</code>
        </span>
      </Button>
    </ButtonGroup>
  );
}
